import React, { useEffect, useMemo, useState } from 'react';
import { X, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { detectSource, openStoreReview, SOURCES } from '@/utils/storeDetector';
import { getReviewStrings } from '@/utils/reviewStrings';

/**
 * Smart In-App Review Modal
 * ---------------------------------------------------------------
 * Behaviour
 *   • First appears 7 days after the app's first open (15s into a session).
 *   • Each time it is shown, the next auto-show is pushed one week ahead
 *     ("Maybe later" / close → again after a week), until the user rates
 *     or taps "Don't ask again". The menu item can open it any time.
 *   • Both store buttons (Huawei / Google) are always shown; the detected
 *     install source is listed first.
 *
 * Persistence (localStorage keys)
 *   • falak.review.state     → 'pending' | 'reviewed' | 'declined'
 *   • falak.review.firstOpen → timestamp of first app open
 *   • falak.review.nextAt    → timestamp before which it won't auto-show
 */

const KEYS = {
  STATE: 'falak.review.state',
  FIRST_OPEN: 'falak.review.firstOpen',
  NEXT_AT: 'falak.review.nextAt',
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 15000;

function getState() {
  try { return localStorage.getItem(KEYS.STATE) || 'pending'; } catch (_) { return 'pending'; }
}
function setState(v) {
  try { localStorage.setItem(KEYS.STATE, v); } catch (_) { /* ignore */ }
}
function postpone() {
  try { localStorage.setItem(KEYS.NEXT_AT, String(Date.now() + WEEK_MS)); } catch (_) { /* ignore */ }
}
function isDue() {
  try {
    let first = parseInt(localStorage.getItem(KEYS.FIRST_OPEN) || '0', 10);
    if (!first) {
      first = Date.now();
      localStorage.setItem(KEYS.FIRST_OPEN, String(first));
    }
    const next = parseInt(localStorage.getItem(KEYS.NEXT_AT) || '0', 10) || first + WEEK_MS;
    return Date.now() >= next;
  } catch (_) { return false; }
}

const SmartReviewModal = ({ forceOpen = false, onClose }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const isRTL = ['ar', 'ur', 'fa'].includes(lang);
  const L = getReviewStrings(lang);

  const source = useMemo(() => detectSource(), []);
  const [open, setOpen] = useState(false);
  const [thanks, setThanks] = useState(false);

  useEffect(() => {
    if (forceOpen) { postpone(); setOpen(true); return; }
    if (getState() !== 'pending' || !isDue()) return;

    const timer = setTimeout(() => {
      postpone();
      setOpen(true);
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [forceOpen]);

  const close = () => {
    setOpen(false);
    setThanks(false);
    if (typeof onClose === 'function') onClose();
  };

  const handleRate = (src) => {
    openStoreReview(src);
    setThanks(true);
    setState('reviewed');
    setTimeout(close, 1800);
  };

  // Next auto-show was already pushed a week ahead when the card opened.
  const handleLater = () => close();

  const handleNever = () => {
    setState('declined');
    close();
  };

  if (!open) return null;

  const btnPrimary = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    padding: '13px 18px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
    color: '#0B132B',
    fontWeight: 700,
    fontSize: 15,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 6px 18px rgba(245, 158, 11, 0.35)',
    letterSpacing: 0.2,
  };

  const btnSecondary = {
    background: 'transparent',
    color: '#94A3B8',
    fontSize: 14,
    padding: '10px 16px',
    border: 'none',
    cursor: 'pointer',
    letterSpacing: 0.2,
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(3, 7, 18, 0.72)', backdropFilter: 'blur(8px)' }}
      onClick={close}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="smart-review-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #14213D 0%, #0B132B 100%)',
          border: '1px solid rgba(245, 158, 11, 0.28)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        }}
        data-testid="smart-review-modal"
      >
        {/* Close button */}
        <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end', padding: '10px 12px 0' }}>
          <button
            onClick={close}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6 }}
            aria-label="Close"
            data-testid="smart-review-close"
          >
            <X size={18} style={{ color: '#94A3B8' }} />
          </button>
        </div>

        {thanks ? (
          /* Thank-you state */
          <div style={{ padding: '10px 28px 36px', textAlign: 'center' }}>
            <div
              style={{
                width: 70, height: 70, margin: '0 auto 16px', borderRadius: '50%',
                background: 'radial-gradient(circle at 35% 35%, #FDE68A 0%, #F59E0B 40%, transparent 75%)',
                boxShadow: '0 0 40px rgba(253, 230, 138, 0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Star size={30} fill="#0B132B" color="#0B132B" />
            </div>
            <h3 style={{ color: '#F1F5F9', fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>{L.thanksTitle}</h3>
            <p style={{ color: '#94A3B8', fontSize: 14, margin: 0 }}>{L.thanksBody}</p>
          </div>
        ) : (
          <div style={{ padding: '4px 28px 28px', textAlign: 'center' }}>
            {/* Star row */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 14 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  size={22}
                  fill="#F59E0B"
                  color="#F59E0B"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(245,158,11,0.55))' }}
                />
              ))}
            </div>

            <h3 style={{ color: '#F1F5F9', fontSize: 20, fontWeight: 700, margin: '0 0 10px' }}>
              {L.title}
            </h3>
            <p style={{ color: '#94A3B8', fontSize: 14, lineHeight: 1.55, margin: '0 0 22px' }}>
              {L.subtitle}
            </p>

            {/* Rate CTAs — both stores always shown; detected store first */}
            <p style={{ color: '#CBD5E1', fontSize: 13, margin: '0 0 12px' }}>{L.chooseStore}</p>
            <div style={{ display: 'flex', flexDirection: source === SOURCES.HUAWEI ? 'column' : 'column-reverse', gap: 10 }}>
              <button style={btnPrimary} onClick={() => handleRate(SOURCES.HUAWEI)} data-testid="smart-review-rate-huawei">
                <Star size={16} fill="#0B132B" color="#0B132B" /> {L.rateHuawei}
              </button>
              <button style={{ ...btnPrimary, background: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)', boxShadow: '0 6px 18px rgba(16, 185, 129, 0.3)' }} onClick={() => handleRate(SOURCES.GOOGLE)} data-testid="smart-review-rate-google">
                <Star size={16} fill="#0B132B" color="#0B132B" /> {L.rateGoogle}
              </button>
            </div>

            <p style={{ color: '#64748B', fontSize: 12, margin: '14px 0 8px' }}>{L.hint}</p>

            {/* Secondary actions */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 4 }}>
              <button style={btnSecondary} onClick={handleLater} data-testid="smart-review-later">{L.later}</button>
              <span style={{ color: '#334155' }}>·</span>
              <button style={btnSecondary} onClick={handleNever} data-testid="smart-review-never">{L.never}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartReviewModal;
